<template>
  <aside
    class="sidenav"
    :class="[
      isOpen ? 'is-open' : 'is-closed',
      isDesktop ? 'is-desktop' : 'is-mobile',
    ]"
    :aria-hidden="!isOpen && !isDesktop"
  >
    <!-- Brand -->
    <div class="brand">
      <img
        :src="brandLogo"
        :alt="restaurantName || 'ENSU'"
        class="brand-logo"
      />
      <div class="brand-text">
        <span class="brand-title">{{ title }}</span>
        <span v-if="restaurantName" class="brand-sub">{{ restaurantName }}</span>
      </div>
      <button
        v-if="!isDesktop"
        class="brand-close"
        aria-label="Menü schließen"
        @click="$emit('close')"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M6 18L18 6" />
        </svg>
      </button>
    </div>

    <!-- Navigation -->
    <nav class="nav">
      <div v-for="(section, sIdx) in filteredNavItems" :key="sIdx" class="section">
        <div v-if="section.title" class="section-title">{{ section.title }}</div>
        <ul class="section-items">
          <li v-for="item in section.items" :key="item.path">
            <router-link
              :to="item.path"
              class="nav-item"
              active-class="is-active"
              @click="onNavClick"
            >
              <span class="nav-icon" v-html="item.icon"></span>
              <span class="nav-label">{{ item.name }}</span>

              <NotificationBadge
                v-if="(item.path === '/waiter-dashboard' || item.path === '/admin/waiter-dashboard') && waiterNotificationCount > 0"
                :count="waiterNotificationCount"
                :animated="hasNewPaymentRequest"
                class="nav-badge"
              />
              <NotificationBadge
                v-else-if="(item.path === '/kitchen' || item.path === '/admin/kitchen') && unreadKitchenOrdersCount > 0"
                :count="unreadKitchenOrdersCount"
                :animated="true"
                class="nav-badge"
                badge-class="bg-red-500 dark:bg-red-600"
              />
              <span
                v-else-if="item.badge"
                :class="item.badge.class"
                class="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
              >{{ item.badge.text }}</span>
            </router-link>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Bottom user card -->
    <div v-if="userName" class="user-card">
      <div class="user-avatar">{{ initials }}</div>
      <div class="user-meta">
        <div class="user-name">{{ userName }}</div>
        <div class="user-role">{{ humanRole }}</div>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { computed, watch, onMounted, onBeforeUnmount, ref } from 'vue'
import { useRoute } from 'vue-router'
import { filterNavItemsByRole } from '@/navigation/sideNavItems'
import { useAuthStore } from '@/stores/auth'
import { useOrderStore } from '@/stores/orders'
import { useRestaurantStore } from '@/stores/restaurant'
import NotificationBadge from '@/components/NotificationBadge.vue'
import ensuLogo from '@/assets/Ensu-logo.png'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  navItems: { type: Array, required: true },
  title: { type: String, default: 'Navigation' },
})
const emit = defineEmits(['close'])

const authStore = useAuthStore()
const orderStore = useOrderStore()
const restaurantStore = useRestaurantStore()
const route = useRoute()

const waiterNotificationCount = computed(() => orderStore.waiterNotificationCount)
const hasNewPaymentRequest = computed(() => orderStore.hasNewPaymentRequest)
const unreadKitchenOrdersCount = computed(() => orderStore.getUngeseheneBestellungenCount)

const userRole = computed(() => {
  if (authStore.user?.role) return authStore.user.role
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}')
    return u?.role || ''
  } catch { return '' }
})

const filteredNavItems = computed(() => filterNavItemsByRole(props.navItems, userRole.value))

const userName = computed(() => authStore.user?.name || '')
const initials = computed(() => {
  const n = userName.value.trim()
  if (!n) return '?'
  return n.split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase()).join('')
})
const humanRole = computed(() => {
  const map = {
    superadmin: 'Super-Admin',
    hotel_group_admin: 'Konzern-Admin',
    admin: 'Direktion',
    restaurant_admin: 'Restaurant-Admin',
    manager: 'Manager',
    kitchen: 'Küche',
    waiter: 'Service',
    staff: 'Service',
  }
  return map[userRole.value] || userRole.value
})
const restaurantName = computed(() => restaurantStore.settings?.name || '')
const brandLogo = computed(() => restaurantStore.logoDataUrl || restaurantStore.settings?.logoImageUrl || ensuLogo)

// Mobile vs. Desktop responsiv
const isDesktop = ref(typeof window !== 'undefined' ? window.innerWidth >= 1024 : true)
function onResize() {
  isDesktop.value = window.innerWidth >= 1024
}

const onNavClick = () => {
  // Auf Mobile schließen wir die Nav nach Klick
  if (!isDesktop.value) emit('close')
}

watch(() => route.path, (newPath) => {
  if (newPath === '/waiter-dashboard' || newPath === '/admin/waiter-dashboard') {
    orderStore.clearWaiterNotifications()
  } else if (newPath === '/kitchen' || newPath === '/admin/kitchen') {
    orderStore.resetUnseenOrdersCount()
  }
}, { immediate: true })

let pollingInterval = null
onMounted(() => {
  window.addEventListener('resize', onResize)
  if (authStore.isAuthenticated) {
    if (['admin', 'waiter', 'manager'].includes(userRole.value?.toLowerCase())) {
      orderStore.fetchOrders()
      pollingInterval = setInterval(() => {
        if (authStore.isAuthenticated) orderStore.fetchOrders()
      }, 30000)
    }
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  if (pollingInterval) clearInterval(pollingInterval)
})
</script>

<style scoped>
.sidenav {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: min(86vw, 304px);
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: saturate(140%) blur(12px);
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  transform: translateX(-104%);
  transition: transform 280ms cubic-bezier(.2,.8,.2,1);
  z-index: 40;
}
.sidenav.is-open {
  transform: translateX(0);
}
.sidenav.is-desktop {
  width: 268px;
  box-shadow: none;
  border-right: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(255, 255, 255, 1);
}
.sidenav.is-desktop.is-closed {
  transform: translateX(-104%);
}

/* ─── Brand ───────────────────────────────────────────── */
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 18px 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.brand-logo {
  height: 36px;
  width: auto;
  flex-shrink: 0;
}
.brand-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}
.brand-title {
  font-size: 13px;
  font-weight: 700;
  color: #0b1f44;
  letter-spacing: 0.2px;
}
.brand-sub {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.brand-close {
  margin-left: auto;
  padding: 6px;
  border-radius: 10px;
  color: #64748b;
  background: transparent;
  transition: background-color 150ms ease;
}
.brand-close:hover { background: rgba(15, 23, 42, 0.06); color: #0b1f44; }
.brand-close svg { width: 22px; height: 22px; }

/* ─── Nav ─────────────────────────────────────────────── */
.nav {
  flex: 1;
  overflow-y: auto;
  padding: 10px 10px 16px;
}
.section + .section { margin-top: 14px; }
.section-title {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: #94a3b8;
  padding: 14px 12px 6px;
}
.section-items { display: flex; flex-direction: column; gap: 2px; list-style: none; padding: 0; margin: 0; }

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  transition: background-color 140ms ease, color 140ms ease;
  user-select: none;
}
.nav-item:hover {
  background-color: rgba(15, 23, 42, 0.05);
  color: #0b1f44;
}
.nav-item.is-active {
  background-color: rgba(11, 31, 68, 0.08);
  color: #0b1f44;
  font-weight: 600;
}

.nav-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.nav-icon :deep(svg) { width: 100%; height: 100%; }

.nav-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nav-badge { position: absolute; right: 10px; top: 8px; }

/* ─── User card ───────────────────────────────────────── */
.user-card {
  margin: 12px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.04);
  display: flex;
  align-items: center;
  gap: 12px;
}
.user-avatar {
  width: 38px; height: 38px; border-radius: 50%;
  background: linear-gradient(135deg, #0b1f44 0%, #1a3a7a 100%);
  color: white;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 13px;
  flex-shrink: 0;
}
.user-meta { min-width: 0; flex: 1; }
.user-name {
  font-size: 13px; font-weight: 600; color: #0b1f44;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.user-role { font-size: 11px; color: #64748b; }
</style>
